import React, { useState, useRef, useEffect, createRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from "@progress/kendo-react-dialogs";
import { FloatingActionButton, Button } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { sendEmail } from '../../actions/index.js';
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { Upload } from "@progress/kendo-react-upload";
import { process } from '@progress/kendo-data-query';
import EditAboutForm from '../../components/EditAboutForm';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@progress/kendo-react-layout';
import Tooltip from '@mui/material/Tooltip';
import { formCloser } from '../../utils/formCloser.js';
import { Input } from '@mui/material';
import { getEmails } from '../../actions/portal/email/getEmails.js';
import { getStoredEmails } from '../../actions/portal/email/getStoredEmails.js';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { CheckmarkCell } from '../../components/GridCell/CheckmarkCell.jsx';
import EmailListModal from '../../components/EmailListModal.jsx';


const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Indent,  
  Undo,
  Redo,
  FontSize,
  FontName,
  Link,
  Unlink,
  InsertImage,
} = EditorTools

const initialDataState = {
  skip: 0,
  take: 10,
  sort: [{ field: 'dateSent', dir: 'desc' }]
}

const DashboardEmailBuilder = () => {
  const { token, user } = useSelector(state => state.authReducer);
  const { users } = useSelector(state => state.usersReducer);
  const { groups } = useSelector(state => state.groupsReducer);
  const { emails, storedEmails } = useSelector(state => state.emailReducer);
  const [dialogProps, setDialogProps] = useState({});
  const [dataState, setDataState] = useState(initialDataState);
  const [files, setFiles] = useState([]);
  const [to, setTo] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [showEmail, setShowEmail] = useState(false);
  const [viewEmails, setViewEmails] = useState(false);
  const [subject, setSubject] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showListView, setShowListView] = useState(false);
  const [targetEmail, setTargetEmail] = useState('');
  const [sort, setSort] = useState([{ field: 'dateSent', dir: 'desc' }]);
  const [emailBody, setEmailBody] = useState('');

  const _exporter = createRef();

  const excelExport = () => {
      if (_exporter.current) {
          _exporter.current.save();
      }
  };

  const onChange = (event) => {
    setEmailBody(event.html);
  }
  
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getEmails(token));
    dispatch(getStoredEmails(token));
  }, [])

  useEffect(() => {
    setFilteredEmails(preProcess(emails, dataState));
  }, [emails])

  const preProcess = () => {
    let processedEmails = [];

    for (const [key, value] of Object.entries(emails)) {
      let opened = 0;
      let delivered = 0;
      let deferred = 0;
      let dropped = 0;
      let bounced = 0;
      value.forEach(v => {
        switch(v.event) {
          case 'open':
            opened++;
            break;
          case 'delivered':
            delivered++;
            break;          
          case 'deferred':
            deferred++;
            break;
          case 'dropped':
            dropped++;
            break;
          case 'bounced':
            bounced++;
            break;
        }
      });

      let subject, sentBy, dateSent, emailTo, emailBcc, emailSubject, emailContent;
      try {
        subject = value[0].category.split("TOPIC-")[1].split(",")[0];
      } catch (e){}

      for (let storedEmail of storedEmails) {
        if (storedEmail.emailSubject == subject) {
          sentBy = storedEmail.sentBy;
          dateSent = storedEmail.dateSent;
          emailTo = storedEmail.toList;
          emailBcc = storedEmail.bccList;
          emailSubject = storedEmail.emailSubject;
          emailContent = storedEmail.emailContent;
        }
      }
      //TODO
      processedEmails.push({ ...value[0], subject: key, opened: opened, delivered: delivered, deferred: deferred, dropped: dropped, bounced: bounced, dateSent, sentBy, emailTo, emailBcc, emailSubject, emailContent });
    }
    return processedEmails.sort((a, b) => new Date(b.event_timestamp) - new Date(a.event_timestamp));
  }

  const emailSubmit = () => {
    dispatch(sendEmail(to, bcc, subject, emailBody, files, token, clearEmailFields))
  }

  const clearEmailFields = () => {
    setFiles([]);
    setTo([]);
    setBcc([]);
    setSubject('');
    setEmailBody('')
  }

  const handleStatClick = (dataItem, title, eventName) => {
    setTargetEmail({ ...dataItem, title, eventName });
    setShowListView(true);
  }

  return (
    <>
      <div style={{ width: '100%', minHeight: '50px' }}>
        <FloatingActionButton
          text={viewEmails ? 'View Email Builder' : 'View Email Statistics'}
          onClick={() => setViewEmails(!viewEmails)}
          align={{ horizontal: 'start', vertical: 'top' }}
          alignOffset={{ x: '30px' }}
          shape='rectangle'
          positionMode='absolute'
          style={{ zIndex: 1000, minWidth: '150px', padding: '10px 20px' }}
        />
        {
          viewEmails && (
            <>
            <Input 
              onChange={(e) => {
                  setFilteredEmails(preProcess(emails, dataState).filter((email) => {
                    return email.subject.toLowerCase().includes(e.target.value.toLowerCase())
                  }
                ))
              }}
              placeholder="Search" 
              sx={{ left: '200px', top: '10px' }}
            />
            <Button type="button" style={{ float: 'right' }} onClick={excelExport}>
              Export to Excel
            </Button>
            <ExcelExport
              fileName={"Email Statistics: " + new Date().toLocaleDateString()}
              data={filteredEmails}
              ref={_exporter}>
                <ExcelExportColumn field="subject" title="Subject" />
                <ExcelExportColumn field="delivered" title="# Delivered" />
                <ExcelExportColumn field="opened" title="# Opened" />
                <ExcelExportColumn field="bounced" title="# Bounced" />
                <ExcelExportColumn field="deferred" title="# Deferred" />
                <ExcelExportColumn field="dropped" title="# Dropped" />
                <ExcelExportColumn field="hasAttachment" title="Has Attachment" />
                <ExcelExportColumn field="sentBy" title="Sent By" />
                <ExcelExportColumn field="dateSent" title="Time Sent" />
            </ExcelExport>
            </>

        )}
      </div>
      {!viewEmails ?
        <Card style={styles.card}>
          <CardHeader className='k-hbox' style={styles.cardHeader}>
            <CardTitle style={styles.cardTitle}>Email Builder</CardTitle>
            <CardSubtitle style={styles.cardSubtitle}>
              To:
              <MultiSelect
                style={{ 
                  width: '80%',
                  paddingLeft: '20px',
                  paddingBottom: '20px',
                  marginRight: '17%'
                }}
                value={to}
                onChange={(e) => setTo(e.value)}
                data={groups.concat(users.map(u => {return { name: u.email, id: u.id, isUser: true }}))}
                textField="name"
                dataItemKey="id"/>
              <div style={{ display: 'inline', paddingRight: '20px' }}>Subject:</div>
              <Input style={{ width: '77%' }} value={subject} onChange={(e) => setSubject(e.target.value)}/>
              <div>
                BCC:
                <MultiSelect
                  style={{ 
                    width: '79.4%',
                    paddingLeft: '20px',
                    paddingBottom: '20px',
                    paddingTop: '20px',
                    marginRight: '17%'
                  }}
                  value={bcc}
                  onChange={(e) => setBcc(e.value)}
                  data={groups.concat(users.map(u => {return { name: u.email, id: u.id, isUser: true }}))}
                  textField="name"
                  dataItemKey="id"/>
              </div>

            </CardSubtitle>
          </CardHeader>
          <CardBody>      
            <Editor 
              value={emailBody}
              onChange={onChange}
              tools={[
                Bold,
                Italic,
                Underline,
                Strikethrough,
                Subscript,
                Superscript,
                AlignLeft,
                AlignCenter,
                AlignRight,
                AlignJustify,
                Indent,  
                Undo,
                Redo,
                FontSize,
                FontName,
                Link,
                Unlink,
                InsertImage,
              ]}
            />
            <CardTitle style={styles.cardTitle}>Attachments</CardTitle>
            <div id="uploadContainer">
              <Upload 
                autoUpload={false}
                withCredentials={false}
                files={files}
                onAdd={(e) => setFiles(e.newState)}
                onRemove={(e) => setFiles(e.newState)}
                onProgress={(e) => setFiles(e.newState)}
                onStatusChange={(e) => setFiles(e.newState)}
              />
            </div>
            { !user.notificationOnly && <FloatingActionButton  
              onClick={emailSubmit}
              text='Send Email'
              align={{ horizontal: 'end', vertical: 'top' }}
              shape='rectangle'
              positionMode='absolute'
              style={{ zIndex: 1000, minWidth: '150px', padding: '10px 20px' }}
            /> }
          </CardBody>
        </Card>
       : 
        <Grid
          {...dataState}
          data={process(filteredEmails, dataState)}
          onDataStateChange={e => setDataState(e.dataState)}
          pageable={true}
          sortable={true}
          autoProcessData={true}
          defaultSort={[{ field: 'dateSent', dir: 'desc' }]}
          className='flo-email-grid'
        >
          <GridColumn field="subject" title="Subject" />
          <GridColumn field="delivered" title="# Delivered" 
            cell={props =>  
              <td onClick={() => handleStatClick(props.dataItem, "Delivered", "delivered")} {...props.tdProps} style={{ color: 'blue', backgroundColor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }} >
                {props.dataItem.delivered}
              </td>
            }
          />
          <GridColumn field="opened" title="# Opened" 
            cell={props =>  
              <td onClick={() => handleStatClick(props.dataItem, "Opened", "open")} {...props.tdProps} style={{ color: 'blue', backgroundColor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }} >
                {props.dataItem.opened}
              </td>
            }
          />
          <GridColumn field="bounced" title="# Bounced" 
            cell={props =>  
              <td onClick={() => handleStatClick(props.dataItem, "Bounced", "bounced")} {...props.tdProps} style={{ color: 'blue', backgroundColor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }} >
                {props.dataItem.bounced}
              </td>
            }
          />
          <GridColumn field="deferred" title="# Deferred" 
            cell={props =>  
              <td onClick={() => handleStatClick(props.dataItem, "Deferred", "deferred")} {...props.tdProps} style={{ color: 'blue', backgroundColor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }} >
                {props.dataItem.deferred}
              </td>
            }
          />
          <GridColumn field="dropped" title="# Dropped" 
            cell={props =>  
              <td onClick={() => handleStatClick(props.dataItem, "Dropped", "dropped")} {...props.tdProps} style={{ color: 'blue', backgroundColor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }} >
                {props.dataItem.dropped}
              </td>
            }
          />
          <GridColumn 
            field="hasAttachment" 
            title="Has Attachment"
            cell={props => <CheckmarkCell checked={props.dataItem.hasAttachment} />}
          />
          <GridColumn 
            field="sentBy" 
            title="Sent By"
          />
          <GridColumn 
            field="dateSent" 
            title="Time Sent"
            cell={props =>  
              <td>
                {props.dataItem.dateSent ? new Date(props.dataItem.dateSent).toLocaleString() : ''}
              </td>
            }
          />
          <GridColumn 
            field="null"
            title="View Email"
            cell={props=>
              <td style={{ overflow: 'visible' }}>
                {props.dataItem.emailTo || props.dataItem.emailBcc || props.dataItem.emailContent || props.dataItem.emailSubject ?
                  <button onClick={() => {setTargetEmail(props.dataItem);setShowEmail(true); setShowListView(true)}} className="k-button k-primary" style={styles.modalBtn}>
                    View Email
                  </button>
                : ''}
              </td>
            }
          />
        </Grid>}
      {showDialog &&
        <Dialog
          {...dialogProps}
          onClose={() => setShowDialog(false)}
        />}
      {showListView &&
        <EmailListModal
          showEmail={showEmail}
          dataItem={targetEmail}
          cancelEdit={() => {setShowListView(false); setShowEmail(false);}}
        />
      }   
    </>
  )
}

const styles = {
  card: {
    minWidth: 800,
    margin: 5,
  },
  cardHeader: {
    flexDirection: 'column',
    minHeight: '185px'
  },
  cardTitle: {
    paddingTop: 10
  },
  cardSubtitle: {
    maxHeight: 100,
    marginTop: 5
  },
  aboutSection: {
    marginBottom: 20
  },
  aboutSectionTitle: {
    fontWeight: 'bold',
    fontSize: 15,
  },
  aboutSectionContent: {
    paddingTop: 5,
    whiteSpace: 'pre-line',
  },
  termsModalContent: {
    whiteSpace: 'pre-line',
  },
  modalBtn: {
    padding: '10px 15px',
  }, 
}

export default DashboardEmailBuilder;